/**
 * Qué datos muestra la tarjeta de vehículo cuando el dueño los configuró desde
 * el builder, y cómo se arma el título que incluye la versión.
 *
 * Hasta ahora la fila de datos estaba escrita a mano dentro de la tarjeta:
 * kilometraje, año, transmisión, combustible y —si el sitio la mostraba— la
 * sucursal. Sin forma de cambiar uno por otro ni de sacar ninguno. El builder
 * ahora expone cinco ranuras (`cardSpec1..5`) y esto es lo que las traduce a la
 * lista de datos que la tarjeta dibuja.
 *
 * ── Lo que este módulo NO hace ──
 * No toca el camino heredado. Una tarjeta sin ranuras configuradas —que son
 * TODAS las páginas guardadas hasta hoy, y también `/vehicles` y el carrusel del
 * home, que nunca mandan ranuras— sigue ejecutando el mismo array escrito a mano
 * de siempre. Ese camino no pasa por acá salvo para pedir la etiqueta de un dato
 * (`specValue`), que es la única pieza compartida: tener dos copias de la tabla
 * de combustibles terminaría en que un combustible nuevo aparece en un modo y en
 * el otro no.
 *
 * O sea: si nadie configura nada, este archivo no cambia una sola pantalla.
 *
 * ── Por qué vive acá ──
 * ESTE ARCHIVO ES EL MISMO EN LOS DOS REPOS — goautos-admin y website-gocar
 * tienen cada uno su implementación de la tarjeta, y sólo coinciden en pantalla
 * si coinciden acá. Si la decisión de qué dato va en cada lugar estuviera en el
 * JSX habría que escribirla dos veces y se irían separando sin que nadie lo note
 * (que es justo lo que pasó con la sucursal: la web la mostraba y la vista previa
 * del builder no). Al tocarlo, copiar el cambio al otro repo;
 * `npm run test:tarjeta-datos` verifica el contrato en ambos.
 *
 * Además es lógica pura, sin React, así que se puede probar en Node.
 */
import { normalizeTraction, normalizeTransmission } from './vehicleSpecs';
import { isBrasil, isUsa, forcedSiteLanguage } from './country';

/** Un dato que la tarjeta sabe mostrar. */
export type SpecKey =
  | 'mileage'
  | 'year'
  | 'transmission'
  | 'fuel'
  | 'traction'
  | 'dealership';

/** Lo que puede tener una ranura: un dato, o nada. */
export type SpecSlot = SpecKey | 'none';

export type CardTitleField =
  | 'model'
  | 'brand'
  | 'brand_model'
  | 'brand_model_version';

/** Cuántas ranuras expone el builder. */
export const SPEC_SLOT_COUNT = 5;

/**
 * Lo que la tarjeta muestra hoy, en el mismo orden. Es el default de cada ranura,
 * así que configurar una sola no cambia las otras cuatro de lugar.
 */
export const DEFAULT_SPEC_SLOTS: readonly SpecSlot[] = [
  'mileage',
  'year',
  'transmission',
  'fuel',
  'dealership',
];

/** Las cinco props que guarda el builder en el nodo de la sección. */
export interface CardSpecSlotProps {
  cardSpec1?: SpecSlot;
  cardSpec2?: SpecSlot;
  cardSpec3?: SpecSlot;
  cardSpec4?: SpecSlot;
  cardSpec5?: SpecSlot;
}

/**
 * El catálogo completo, en el orden en que se le ofrece al dueño en el builder.
 * Exportado para que el panel de settings del admin no pueda ofrecer un dato que
 * el renderer no sabe dibujar: `npm run test:tarjeta-datos` compara las dos
 * listas.
 */
export const SPEC_KEYS: readonly SpecKey[] = [
  'mileage',
  'year',
  'transmission',
  'fuel',
  'traction',
  'dealership',
];

const isSpecKey = (valor: unknown): valor is SpecKey =>
  typeof valor === 'string' && (SPEC_KEYS as readonly string[]).includes(valor);

/**
 * Props guardadas → lista de datos a dibujar.
 *
 * `configured` es lo que decide TODO: en false la tarjeta ni mira `keys` y sigue
 * por su camino de siempre. Sólo pasa a true cuando el builder guardó al menos
 * una ranura, o sea cuando alguien entró al panel y eligió.
 *
 * Una ranura ausente cae a su default, no desaparece: el panel escribe sólo la
 * ranura que se tocó, así que cambiar el dato 3 no puede borrar los otros cuatro.
 *
 * Duplicados: gana la primera aparición. Elegir "Año" en la ranura 2 y otra vez
 * en la 4 muestra el año una sola vez, y la ranura 4 queda vacía — es lo mismo
 * que hubiera pasado con dos chips idénticos, sin el chip repetido.
 */
export const resolveSpecSlots = (
  props: CardSpecSlotProps | undefined | null
): { configured: boolean; keys: SpecKey[] } => {
  // `unknown` a propósito: esto llega de un JSON guardado hace meses por una
  // versión del admin que puede no ser la de ahora. El tipo declara la
  // intención; lo que de verdad viene se valida acá abajo.
  const guardadas: unknown[] = [
    props?.cardSpec1,
    props?.cardSpec2,
    props?.cardSpec3,
    props?.cardSpec4,
    props?.cardSpec5,
  ];
  // Una cadena vacía no cuenta como configuración: es lo que deja un select al
  // que se le borró el valor, no una decisión del dueño.
  const elegida = (valor: unknown): valor is string =>
    typeof valor === 'string' && valor !== '';
  const configured = guardadas.some(elegida);

  const claves: SpecKey[] = [];
  for (let i = 0; i < SPEC_SLOT_COUNT; i++) {
    const cruda = guardadas[i];
    const ranura: string = elegida(cruda) ? cruda : DEFAULT_SPEC_SLOTS[i];
    if (ranura === 'none') continue;
    if (!isSpecKey(ranura)) continue; // valor desconocido (repo desactualizado)
    if (claves.includes(ranura)) continue; // duplicado: gana el primero
    claves.push(ranura);
  }
  return { configured, keys: claves };
};

// ── Etiquetas ──

/**
 * Todo lo que entra viene de la base o de un JSON guardado, así que puede no
 * ser una cadena aunque el tipo diga que sí. `String()` antes de tocarlo: un
 * `.trim()` sobre un número tira la tarjeta entera abajo.
 */
const texto = (valor: unknown): string => String(valor ?? '').trim();

type FuelEN = 'Gasoline' | 'Diesel' | 'Hybrid' | 'Electric' | 'Gas' | 'Flex' | 'Ethanol';
const normalizeFuel = (name?: unknown): FuelEN | undefined => {
  const k = texto(name).toLowerCase();
  if (['gasoline', 'gasolina', 'bencina', 'nafta', 'petrol'].includes(k)) return 'Gasoline';
  if (['diesel', 'diésel', 'diseal', 'dísel'].includes(k)) return 'Diesel';
  if (['hybrid', 'híbrido', 'hibrido'].includes(k)) return 'Hybrid';
  if (['electric', 'eléctrico', 'electrico', 'ev'].includes(k)) return 'Electric';
  if (['gas', 'lpg', 'gnv', 'gpl', 'gás'].includes(k)) return 'Gas';
  // Combustibles del catálogo para Brasil (antes caían al fallback '—').
  if (['flex'].includes(k)) return 'Flex';
  if (['etanol', 'ethanol', 'álcool', 'alcool'].includes(k)) return 'Ethanol';
  return undefined;
};

type TransEN = 'Manual' | 'Automatic' | 'CVT' | 'Semi-Automatic' | 'Tiptronic' | 'Dual-Clutch';
const normalizeTrans = (name?: unknown): TransEN | undefined => {
  const k = texto(name).toLowerCase();
  if (['manual', 'mecánica', 'mecanica', 'stick'].includes(k)) return 'Manual';
  if (['automatic', 'automática', 'automatica', 'auto'].includes(k)) return 'Automatic';
  if (['cvt'].includes(k)) return 'CVT';
  if (['semi-automatic', 'semiautomática', 'semiautomatica'].includes(k)) return 'Semi-Automatic';
  if (['tiptronic'].includes(k)) return 'Tiptronic';
  if (['dct', 'dual clutch', 'doble embrague'].includes(k)) return 'Dual-Clutch';
  return undefined;
};

const fuelEs: Record<FuelEN, string> = {
  Gasoline: 'Gasolina',
  Diesel: 'Diésel',
  Hybrid: 'Híbrido',
  Electric: 'Eléctrico',
  Gas: 'Gas',
  Flex: 'Flex',
  Ethanol: 'Etanol',
};

const transEs: Record<TransEN, string> = {
  Manual: 'Manual',
  Automatic: 'Automática',
  CVT: 'CVT',
  'Semi-Automatic': 'Semiautomática',
  Tiptronic: 'Tiptronic',
  'Dual-Clutch': 'Doble embrague',
};

/**
 * Las mismas dos tablas en portugués, SÓLO para sitios de Brasil
 * (`ctx.country`). Sin país, o con cualquier otro, se usan las de arriba: un
 * sitio chileno no cambia una letra.
 */
const fuelPt: Record<FuelEN, string> = {
  Gasoline: 'Gasolina',
  Diesel: 'Diesel',
  Hybrid: 'Híbrido',
  Electric: 'Elétrico',
  Gas: 'GNV',
  Flex: 'Flex',
  Ethanol: 'Etanol',
};

const transPt: Record<TransEN, string> = {
  Manual: 'Manual',
  Automatic: 'Automático',
  CVT: 'CVT',
  'Semi-Automatic': 'Semiautomático',
  Tiptronic: 'Tiptronic',
  'Dual-Clutch': 'Dupla embreagem',
};

/**
 * Las mismas dos tablas en inglés, SÓLO para sitios de EE.UU. (`ctx.country`).
 * 'Gas' es GNV/GLP: en EE.UU. "gas" es la nafta, así que va con su nombre.
 */
const fuelEn: Record<FuelEN, string> = {
  Gasoline: 'Gasoline',
  Diesel: 'Diesel',
  Hybrid: 'Hybrid',
  Electric: 'Electric',
  Gas: 'CNG/LPG',
  Flex: 'Flex fuel',
  Ethanol: 'Ethanol',
};

const transEn: Record<TransEN, string> = {
  Manual: 'Manual',
  Automatic: 'Automatic',
  CVT: 'CVT',
  'Semi-Automatic': 'Semi-automatic',
  Tiptronic: 'Tiptronic',
  'Dual-Clutch': 'Dual-clutch',
};

/**
 * Tracción: corta, porque va en un chip al lado de otros tres. El panel de
 * filtros usa 'AWD (integral)' y 'FWD (delantera)', que ahí entran y acá no.
 */
const tractionEs: Record<string, string> = {
  '4x2': '4x2',
  '4x4': '4x4',
  awd: 'AWD',
  fwd: 'FWD',
};

const capitalizar = (crudo: string) =>
  crudo.charAt(0).toUpperCase() + crudo.slice(1).toLowerCase();

/** Vehículo visto por la tarjeta. Estructural a propósito: los dos repos tienen
 *  su propio tipo `Vehicle` y ninguno de los dos es importable desde el otro. */
export interface VehicleForCard {
  brand?: { name?: string | null } | null;
  model?: { name?: string | null } | null;
  fuel_type?: { name?: string | null } | null;
  year?: number | null;
  mileage?: number | null;
  transmission?: string | null;
  traction?: string | null;
  version_name?: string | null;
}

/** Lo que la tarjeta ya resolvió y el módulo no puede calcular solo. */
export interface SpecContext {
  /**
   * Sucursal ya formateada y ya filtrada por el interruptor del sitio
   * (Configuración → Sucursales). Cadena vacía = no se muestra. Se pasa hecha
   * porque depende del cliente cargado, no del vehículo, y porque el formato
   * ('Sucursal principal' → la ciudad) vive en `dealershipLabel`.
   */
  dealershipLabel?: string;
  /**
   * País del tenant (`clients.country`). Opcional: sólo 'BR' (etiquetas en
   * portugués y miles con pt-BR) y 'US' (etiquetas en inglés) cambian algo.
   * Ausente = lo de siempre.
   */
  country?: string | null;
}

/**
 * El texto de un dato, o `null` si ese vehículo no lo tiene.
 *
 * La usan los DOS caminos: el heredado le pone `?? '—'` encima —que es
 * exactamente lo que hacía escrito a mano— y el configurado directamente no
 * dibuja el chip. Es la única pieza que comparten, y a propósito: con dos copias
 * de la tabla de combustibles, agregar uno nuevo lo haría aparecer en un modo y
 * en el otro no.
 *
 * Combustible cae al texto crudo cuando no lo reconoce: la lista de
 * equivalencias no cubre todos los catálogos y el dato de la base siempre es
 * mejor que esconderlo. Tracción hace lo mismo por el mismo motivo.
 */
export const specValue = (
  vehiculo: VehicleForCard | undefined | null,
  clave: SpecKey,
  ctx: SpecContext = {}
): string | null => {
  if (!vehiculo) return null;
  const br = isBrasil(ctx.country);
  // EE.UU.: transmisión y combustible en inglés, y los miles con coma
  // (45,900). La unidad sigue en km: las millas van en otro lote.
  const us = forcedSiteLanguage(ctx.country) === 'en';
  switch (clave) {
    case 'mileage':
      return typeof vehiculo.mileage === 'number'
        ? `${vehiculo.mileage.toLocaleString(br ? 'pt-BR' : us ? 'en-US' : 'es-CL')} km`
        : null;

    case 'year':
      return vehiculo.year ? String(vehiculo.year) : null;

    case 'transmission': {
      const norm = normalizeTrans(vehiculo.transmission);
      if (norm) return (br ? transPt : us ? transEn : transEs)[norm];
      // Sólo Brasil y EE.UU.: lo que esta tabla no reconoce ('MECANICO',
      // 'AUTOMATICO'…) pasa por el normalizador de los filtros. En Chile, null
      // como siempre.
      if (br || us) {
        const slug = normalizeTransmission(vehiculo.transmission);
        return slug ? (br ? transPt : transEn)[slug === 'manual' ? 'Manual' : 'Automatic'] : null;
      }
      return null;
    }

    case 'fuel': {
      const norm = normalizeFuel(vehiculo.fuel_type?.name);
      if (norm) return (br ? fuelPt : us ? fuelEn : fuelEs)[norm];
      const crudo = texto(vehiculo.fuel_type?.name);
      return crudo ? capitalizar(crudo) : null;
    }

    case 'traction': {
      const norm = normalizeTraction(vehiculo.traction);
      if (norm) return tractionEs[norm];
      const crudo = texto(vehiculo.traction);
      return crudo ? capitalizar(crudo) : null;
    }

    case 'dealership':
      return texto(ctx.dealershipLabel) || null;

    default:
      return null;
  }
};

/**
 * Título "Marca + Modelo + Versión" — la opción nueva, y la única que pasa por
 * acá: las tres de antes siguen armándose donde estaban, sin tocarse.
 *
 * `version_name` está cargada en el 22% del stock, así que la degradación no es
 * un caso de borde sino lo habitual: sin versión el título queda en marca +
 * modelo, sin espacios colgando ni separadores sueltos.
 */
export const buildVersionTitle = (
  vehiculo: VehicleForCard | undefined | null,
  /** País del tenant: EE.UU. rotula el respaldo en inglés ('Make'); el resto, 'Marca' como siempre. */
  country?: string | null
): string =>
  [
    texto(vehiculo?.brand?.name) || (isUsa(country) ? 'Make' : 'Marca'),
    texto(vehiculo?.model?.name),
    texto(vehiculo?.version_name),
  ]
    .filter((parte) => parte !== '')
    .join(' ');
